import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '../components/feature/Navbar';
import Footer from '../components/feature/Footer';
import { useSEO } from '../hooks/useSEO';

const quickLinks = [
  { icon: 'ri-stock-line',        label: 'VPS Trader Brasil',    path: '/vps-trader-br',  tag: 'B3 · Bovespa' },
  { icon: 'ri-stock-line',        label: 'VPS Trader Londres',   path: '/vps-trader-uk',  tag: 'LSE · Forex' },
  { icon: 'ri-stock-line',        label: 'VPS Trader EUA',       path: '/vps-trader-us',  tag: 'NYSE · NASDAQ' },
  { icon: 'ri-code-s-slash-line', label: 'VPS para Devs',        path: '/vps-dev',        tag: 'Root · NVMe' },
  { icon: 'ri-user-line',         label: 'VPS Pessoal',          path: '/vps-pessoal',    tag: 'A partir R$ 49' },
  { icon: 'ri-building-2-line',   label: 'VPS Empresa',          path: '/vps-empresa',    tag: 'SLA Corporativo' },
  { icon: 'ri-server-line',       label: 'Data Centers',         path: '/data-center',    tag: 'Tier 3 · Global' },
  { icon: 'ri-information-line',  label: 'Quem Somos',           path: '/sobre',          tag: 'Desde 2008' },
];

export default function NotFound() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tick, setTick] = useState(true);
  const [glitch, setGlitch] = useState(false);

  useSEO({
    title: 'MUSTHOST | 404 — Página não encontrada',
    description: 'A página que você tentou acessar não existe ou foi movida. Acesse nossos planos de VPS para traders, desenvolvedores e empresas.',
    keywords: 'VPS trader, VPS desenvolvedor, VPS empresa, musthost, servidor VPS Brasil, VPS alta performance, hospedagem VPS, data center Tier 3',
    noIndex: true,
  });

  useEffect(() => {
    const cursor = setInterval(() => setTick((t) => !t), 600);
    return () => clearInterval(cursor);
  }, []);

  useEffect(() => {
    const trigger = setInterval(() => {
      setGlitch(true);
      setTimeout(() => setGlitch(false), 200);
    }, 4000);
    return () => clearInterval(trigger);
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      {/* ── HERO ── */}
      <section
        className="relative flex-1 flex items-center justify-center overflow-hidden pt-16"
        style={{ background: 'linear-gradient(135deg, #0a0f1e 0%, #0d1b3e 50%, #0C1A6B 100%)', minHeight: '92vh' }}
      >
        {/* Grid background */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        />
        {/* Radial glow */}
        <div
          className="absolute inset-0 pointer-events-none opacity-30"
          style={{
            backgroundImage:
              'radial-gradient(ellipse 60% 50% at 50% 40%, #1434A4 0%, transparent 70%)',
          }}
        />

        <div className="relative z-10 max-w-5xl mx-auto px-6 py-20 flex flex-col items-center text-center">

          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/8 border border-white/15 text-white/70 text-xs font-semibold px-4 py-2 rounded-full mb-8 uppercase tracking-widest">
            <i className="ri-error-warning-line text-amber-400" />
            Erro HTTP 404 — Recurso não localizado
          </div>

          {/* Giant 404 */}
          <div className="relative mb-6 select-none">
            <span
              className={`text-[8rem] md:text-[14rem] font-black leading-none tracking-tighter transition-all duration-75 ${
                glitch ? 'text-red-400 translate-x-1' : 'text-white/10'
              }`}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              404
            </span>
            {/* Overlay colored text */}
            <span
              className={`absolute inset-0 flex items-center justify-center text-[8rem] md:text-[14rem] font-black leading-none tracking-tighter transition-all duration-75 pointer-events-none ${
                glitch ? '-translate-x-1 text-blue-400/80' : 'text-white/[0.06]'
              }`}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              404
            </span>
            {/* Center icon */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-20 h-20 md:w-28 md:h-28 flex items-center justify-center rounded-2xl bg-white/8 border border-white/15 backdrop-blur-sm">
                <i className="ri-server-line text-4xl md:text-5xl text-white/60" />
              </div>
            </div>
          </div>

          {/* Heading */}
          <h1
            className="text-2xl md:text-4xl font-bold text-white mb-4"
            style={{ letterSpacing: '-0.02em' }}
          >
            Página não encontrada
          </h1>
          <p className="text-blue-200/70 text-base md:text-lg max-w-lg leading-relaxed mb-8">
            O endereço que você acessou não existe, foi movido ou digitado incorretamente.
          </p>

          {/* Terminal-style path display */}
          <div className="inline-flex items-center gap-2 bg-black/40 border border-white/10 rounded-lg px-5 py-3 mb-10 max-w-full overflow-hidden">
            <i className="ri-terminal-box-line text-green-400 text-sm shrink-0" />
            <span className="text-gray-500 text-sm font-mono whitespace-nowrap">GET</span>
            <span className="text-red-400 text-sm font-mono truncate max-w-[260px] md:max-w-none">
              {location.pathname}
            </span>
            <span className="text-gray-500 text-sm font-mono whitespace-nowrap">→ 404</span>
            <span className={`inline-block w-2 h-4 bg-white/70 rounded-sm ml-1 transition-opacity duration-100 ${tick ? 'opacity-100' : 'opacity-0'}`} />
          </div>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row items-center gap-3 mb-6">
            <button
              onClick={() => navigate('/')}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white text-gray-900 text-sm font-bold rounded-xl hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line" />
              Voltar para o início
            </button>
            <button
              onClick={() => navigate('/contato')}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white/8 border border-white/20 text-white text-sm font-semibold rounded-xl hover:bg-white/15 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-customer-service-2-line" />
              Falar com suporte
            </button>
            <button
              onClick={() => navigate(-1)}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white/5 border border-white/10 text-white/60 text-sm font-semibold rounded-xl hover:bg-white/10 hover:text-white transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              Voltar
            </button>
          </div>

          {/* Status indicator */}
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <span className="relative flex w-2 h-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-40" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
            </span>
            Todos os sistemas operacionais — musthost.com.br está no ar
          </div>
        </div>
      </section>

      {/* ── QUICK LINKS ── */}
      <section className="bg-white border-t border-gray-100 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Talvez você esteja procurando</p>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
              Páginas mais acessadas
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {quickLinks.map((link) => (
              <button
                key={link.path}
                onClick={() => navigate(link.path)}
                className="group flex flex-col items-start gap-3 p-4 bg-gray-50 border border-gray-200 rounded-xl hover:border-gray-400 hover:bg-white transition-all cursor-pointer text-left"
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-200 group-hover:bg-gray-900 transition-colors">
                  <i className={`${link.icon} text-gray-600 group-hover:text-white text-base transition-colors`} />
                </div>
                <div>
                  <p className="text-gray-900 text-sm font-semibold leading-tight mb-0.5">{link.label}</p>
                  <p className="text-gray-400 text-xs">{link.tag}</p>
                </div>
                <div className="mt-auto self-end">
                  <i className="ri-arrow-right-line text-gray-300 group-hover:text-gray-700 group-hover:translate-x-1 transition-all text-sm" />
                </div>
              </button>
            ))}
          </div>

          {/* Bottom CTA strip */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 bg-gray-900 rounded-2xl px-6 py-5">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-xl shrink-0">
                <i className="ri-whatsapp-line text-green-400 text-xl" />
              </div>
              <div>
                <p className="text-white text-sm font-semibold">Não encontrou o que precisava?</p>
                <p className="text-gray-500 text-xs mt-0.5">Fale com nossa equipe técnica — suporte em PT-BR, 24/7.</p>
              </div>
            </div>
            <a
              href="https://api.whatsapp.com/send?phone=5585986990114"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-white text-gray-900 text-sm font-bold rounded-xl hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap shrink-0"
            >
              WhatsApp &nbsp;+55 (85) 98699-0114
              <i className="ri-external-link-line text-xs text-gray-500" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
